// src/AssetInspection.js
import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "./api";

/**
 * Asset Inspection (mobile/web)
 * - Select asset via Search (picker) or Scan QR
 * - Add meter reading + inspection notes
 * - Save -> backend logs + triggers meter/date follow-ups + returns summaries
 */
export default function AssetInspection({ site }) {
  const [assetOpen, setAssetOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);

  const [asset, setAsset] = useState(null); // { asset_code, description, manufacturer, model_number, meter_unit, ...}
  const [assetErr, setAssetErr] = useState("");

  const [meter, setMeter] = useState("");
  const [items, setItems] = useState([]); // string[]
  const [draft, setDraft] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");

  // summaries returned by backend
  const [meterSummary, setMeterSummary] = useState(null); // { triggered_due:[], upcoming:[], note }
  const [dateSummary, setDateSummary] = useState(null);   // { due_now:[], upcoming:[] }

  const [savedOnce, setSavedOnce] = useState(false);


  useEffect(() => {
    // when asset changes, reset form + summaries
    setMeter("");
    setItems([]);
    setDraft("");
    setSaveErr("");
    setMeterSummary(null);
    setDateSummary(null);
    setSavedOnce(false);
  }, [asset?.asset_code]);

  const addItem = () => {
    const v = (draft || "").trim();
    if (!v) return;
    setItems((prev) => [...prev, v]);
    setDraft("");
  };

  const editItem = (idx) => {
    const old = items[idx] || "";
    const next = window.prompt("Edit inspection item:", old);
    if (next == null) return;
    const v = next.trim();
    if (!v) return;
    setItems((prev) => prev.map((x, i) => (i === idx ? v : x)));
  };

  const removeItem = (idx) => {
    if (!window.confirm("Remove this inspection item?")) return;
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaveErr("");
    setAssetErr("");
    if (!site) return setSaveErr("Site missing.");
    if (!asset?.asset_code) return setSaveErr("Select an asset first.");

    // block if user typed but didn’t add
    if ((draft || "").trim()) {
      return setSaveErr("You typed an inspection item but didn’t add it to the list. Click “Add to List” first.");
    }

      // meter optional (desktop-equivalent prompts)
    const meterText = (meter || "").trim();
    let meterVal = null;

    if (!meterText) {
      const ok = window.confirm(
        "No meter reading entered.\n\nContinue without meter data?"
      );
      if (!ok) return;
    } else {
      const n = Number(meterText);
      if (!Number.isFinite(n)) return setSaveErr("Meter reading must be a number (or leave blank).");

      const ok = window.confirm(
        `Confirm Meter Reading:\n\nMeter reading entered: ${n}${asset?.meter_unit ? ` ${asset.meter_unit}` : ""}\n\nIs this correct before saving the inspection?`
      );
      if (!ok) return;

      meterVal = n;
    }


    setSaving(true);
    try {
      const r = await apiFetch("/api/asset/inspection/save", {
        method: "POST",
        body: JSON.stringify({
          site,
          asset_code: asset.asset_code,
          meter_value: meterVal,
          meter_unit: (asset.meter_unit || "").trim() || null,
          inspection_items: items, // list of notes
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      const out = await r.json();
      if (out?.warnings?.length) {
        setSaveErr(`Saved with warnings:\n${out.warnings.join("\n")}`);
      }

      setMeterSummary(out?.meter_summary || null);
      setDateSummary(out?.date_summary || null);
      setSavedOnce(true);
    } catch (e) {
      setSaveErr(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="text-2xl font-semibold">Asset Inspection @ {site}</div>
      </div>

      {/* Top select bar */}
      <div className="border rounded-lg p-4 bg-white shadow-sm mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <button
            className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
            type="button"
            onClick={() => setAssetOpen(true)}
          >
            🔎 Search Asset
          </button>

          <button
            className="px-3 py-2 rounded bg-purple-600 text-white hover:bg-purple-700"
            type="button"
            onClick={() => setScanOpen(true)}
          >
            📷 Scan Asset QR
          </button>

          <button
            className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300"
            type="button"
            onClick={() => setAsset(null)}
            disabled={!asset}
          >
            Clear
          </button>

          <div className="ml-auto text-sm text-gray-600">
            {asset ? (
              <span>
                Selected: <span className="font-mono">{asset.asset_code}</span> — {asset.description || ""}
              </span>
            ) : (
              "No asset selected"
            )}
          </div>
        </div>

        {assetErr && <div className="text-red-600 mt-2">{assetErr}</div>}
      </div>

      {/* Main content */}
      {!asset ? (
        <div className="text-sm text-gray-600">Select an asset to start an inspection.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: inspection entry */}
          <div className="lg:col-span-2 border rounded-lg p-4 bg-white shadow-sm">
            <div className="text-lg font-semibold mb-2">Equipment Inspection</div>
            <div className="text-sm text-gray-600 mb-4">
              Record the current meter reading (optional) and any issues noticed. Follow-ups happen at next maintenance
              or via a new WO.
            </div>

            {/* Asset info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-4">
              <Info label="Asset Code" value={asset.asset_code} mono />
              <Info label="Description" value={asset.description || "—"} />
              <Info label="Manufacturer" value={asset.manufacturer || "—"} />
              <Info label="Model" value={asset.model_number || asset.model || "—"} />
            </div>

            {/* Meter */}
            <div className="border rounded p-3 mb-4">
              <div className="text-xs text-gray-600 mb-1">
                Meter Reading {asset.meter_unit ? `(${asset.meter_unit})` : "(optional)"}
              </div>
              <input
                className="border rounded px-2 py-2 w-48"
                value={meter}
                onChange={(e) => setMeter(e.target.value)}
                inputMode="decimal"
                placeholder="e.g. 1234"
              />
            </div>

            {/* Items */}
            <div className="border rounded p-3">
              <div className="font-semibold mb-2">Inspection Follow-Up Items</div>

              <div className="flex gap-2 mb-3">
                <input
                  className="border rounded px-2 py-2 w-full"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Type issue noticed…"
                />
                <button
                  className="px-3 py-2 rounded bg-orange-500 text-white hover:bg-orange-600"
                  type="button"
                  onClick={addItem}
                >
                  Add to List
                </button>
              </div>

              <div className="text-xs text-gray-500 mb-2">Tip: click an item to edit; use ✖ to remove.</div>

              <div className="border rounded overflow-auto max-h-[320px]">
                {items.map((t, idx) => (
                  <div
                    key={`${idx}-${t}`}
                    className={`flex items-center gap-2 px-3 py-2 border-b ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                  >
                    <button className="text-left flex-1" type="button" onClick={() => editItem(idx)}>
                      {t}
                    </button>
                    <button className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300" type="button" onClick={() => removeItem(idx)}>
                      ✖
                    </button>
                  </div>
                ))}
                {items.length === 0 && <div className="p-3 text-sm text-gray-600">No items added.</div>}
              </div>
            </div>

            {saveErr && <div className="text-red-600 mt-3">{saveErr}</div>}

            <div className="mt-4">
              <button
                className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                type="button"
                onClick={save}
                disabled={saving || savedOnce}
              >
                {saving ? "Saving…" : savedOnce ? "Saved" : "Save Inspection"}
              </button>
            </div>
          </div>

          {/* Right: summaries */}
          <div className="border rounded-lg p-4 bg-white shadow-sm">
            <div className="text-lg font-semibold mb-2">Due Summary</div>

            <div className="border rounded p-3 mb-3">
              <div className="font-semibold mb-1">Meter-Based Due Summary</div>
              {!meterSummary ? (
                <div className="text-sm text-gray-600">Summary will appear after Save.</div>
              ) : (
                <>
                  <div className="text-sm">
                    <div className="text-gray-600 text-xs">Saved Notes</div>
                    <div>{meterSummary.saved_notes || "(none)"}</div>
                  </div>

                  <div className="mt-2 text-sm">
                    <div className="text-gray-600 text-xs">Thresholds</div>
                    <div className={meterSummary.triggered_due?.length ? "text-orange-600 font-semibold" : "text-gray-600"}>
                      {meterSummary.triggered_due?.length
                        ? `DUE NOW at ${meterSummary.triggered_due.join(", ")}`
                        : "None due now"}
                    </div>
                    <div className={meterSummary.upcoming?.length ? "text-orange-600" : "text-gray-600"}>
                      {meterSummary.upcoming?.length ? `Upcoming at ${meterSummary.upcoming.join(", ")}` : ""}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="border rounded p-3">
              <div className="font-semibold mb-1">Date-Based Due Summary</div>
              {!dateSummary ? (
                <div className="text-sm text-gray-600">Summary will appear after Save.</div>
              ) : (
                <>
                  <div className="text-sm">
                    <div className="text-gray-600 text-xs">Due Now</div>
                    <div className={dateSummary.due_now?.length ? "text-red-600 font-semibold" : "text-gray-600"}>
                      {dateSummary.due_now?.length ? dateSummary.due_now.join(", ") : "None"}
                    </div>
                  </div>
                  <div className="mt-2 text-sm">
                    <div className="text-gray-600 text-xs">Upcoming</div>
                    <div className={dateSummary.upcoming?.length ? "text-orange-600" : "text-gray-600"}>
                      {dateSummary.upcoming?.length ? dateSummary.upcoming.join(", ") : "None"}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <AssetPickerModal
        open={assetOpen}
        onClose={() => setAssetOpen(false)}
        site={site}
        onPick={async (asset_code) => {
          setAssetOpen(false);
          if (!asset_code) return;
          // fetch full asset details
          try {
            const qs = new URLSearchParams({ site, asset_code });
            const r = await apiFetch(`/api/asset-inspection/asset/get?${qs}`);
            if (!r.ok) throw new Error(await r.text());
            setAsset(await r.json());
          } catch (e) {
            const msg = String(e?.message || e);
            // if backend returned {"detail": "..."} as text, try to clean it
            const cleaned = msg.includes('"detail"')
              ? (() => { try { return JSON.parse(msg).detail || msg; } catch { return msg; } })()
              : msg;
          
            setAsset(null);
            setAssetErr(cleaned);
          }
        }}
      />

      <AssetQRScanModal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onScanned={async (code) => {
          setScanOpen(false);
          const asset_code = String(code || "").trim();
          if (!asset_code) return;
          try {
            const qs = new URLSearchParams({ site, asset_code });
            const r = await apiFetch(`/api/asset-inspection/asset/get?${qs}`);
            if (!r.ok) throw new Error(await r.text());
            setAsset(await r.json());
          } catch (e) {
            const msg = String(e?.message || e);
            // if backend returned {"detail": "..."} as text, try to clean it
            const cleaned = msg.includes('"detail"')
              ? (() => { try { return JSON.parse(msg).detail || msg; } catch { return msg; } })()
              : msg;
          
            setAsset(null);
            setAssetErr(cleaned);
          }
        }}
      />
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-gray-600 text-xs">{label}</div>
      <div className={`border rounded px-2 py-2 bg-white ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

/* =========================
 * Asset Picker (reuse equipment picker endpoint)
 * ========================= */
function AssetPickerModal({ open, onClose, site, onPick }) {
  const [assetCode, setAssetCode] = useState("");
  const [desc, setDesc] = useState("");
  const [mfg, setMfg] = useState("");
  const [model, setModel] = useState("");
  const [cat, setCat] = useState("");
  const [job, setJob] = useState("");

  const [opt, setOpt] = useState({ descriptions: [], manufacturers: [], models: [], categories: [], job_codes: [] });
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    let alive = true;

    (async () => {
      try {
        const qs = new URLSearchParams({ site });
        const r = await apiFetch(`/api/asset-inspection/pickers/assets/options?${qs}`);
        const data = r.ok ? await r.json() : null;
        if (!alive) return;
        setOpt({
          descriptions: data?.descriptions || [],
          manufacturers: data?.manufacturers || [],
          models: data?.models || [],
          categories: data?.categories || [],
          job_codes: data?.job_codes || [],
        });
      } catch {
        if (!alive) return;
        setOpt({ descriptions: [], manufacturers: [], models: [], categories: [], job_codes: [] });
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, site]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
  
    (async () => {
      setErr("");
      try {
        const qs = new URLSearchParams({
          site,
          asset_code: assetCode,
          description: desc,
          manufacturer: mfg,
          model_number: model,
          category: cat,
          job_code: job,
        });
  
        const url = `/api/asset-inspection/pickers/assets?${qs}`;
  
        const r = await apiFetch(url);
  
        // ✅ If backend responded but not OK, show status + body
        if (!r.ok) {
          const txt = await r.text().catch(() => "");
          if (!alive) return;
          setErr(`HTTP ${r.status}: ${txt || "Request failed"}`);
          setRows([]);
          return;
        }
  
        // ✅ If OK but body isn't JSON, catch parse errors
        let data = [];
        try {
          data = await r.json();
        } catch (e) {
          const txt = await r.text().catch(() => "");
          if (!alive) return;
          setErr(`Bad JSON from server: ${txt || String(e)}`);
          setRows([]);
          return;
        }
  
        if (!alive) return;
        setRows(data || []);
      } catch (e) {
        // ✅ This is your current failure path (network/CORS/mixed-content)
        if (!alive) return;
        setErr(`Fetch failed: ${String(e?.message || e)}`);
        setRows([]);
      }
    })();
  
    return () => {
      alive = false;
    };
  }, [open, site, assetCode, desc, mfg, model, cat, job]);


  useEffect(() => {
    if (!open) return;
    setAssetCode("");
    setDesc("");
    setMfg("");
    setModel("");
    setCat("");
    setJob("");
    setErr("");
    setRows([]);
  }, [open]);

  if (!open) return null;

  return (
    <Modal title="Select Asset" onClose={onClose}>
      {err && <div className="text-red-600 mb-2">{err}</div>}

      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-3">
        <input className="border rounded px-2 py-2" placeholder="Asset Code" value={assetCode} onChange={(e) => setAssetCode(e.target.value)} />

        <select className="border rounded px-2 py-2" value={desc} onChange={(e) => setDesc(e.target.value)}>
          <option value="">All Descriptions</option>
          {opt.descriptions.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>

        <select className="border rounded px-2 py-2" value={mfg} onChange={(e) => setMfg(e.target.value)}>
          <option value="">All Manufacturers</option>
          {opt.manufacturers.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>

        <select className="border rounded px-2 py-2" value={model} onChange={(e) => setModel(e.target.value)}>
          <option value="">All Models</option>
          {opt.models.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>

        <select className="border rounded px-2 py-2" value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">All Categories</option>
          {opt.categories.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>

        <select className="border rounded px-2 py-2" value={job} onChange={(e) => setJob(e.target.value)}>
          <option value="">All Job Codes</option>
          {opt.job_codes.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      <div className="max-h-96 overflow-auto border rounded">
        {rows.map((r) => (
          <button
            key={r.asset_code}
            className="w-full text-left px-3 py-2 border-b hover:bg-gray-50"
            type="button"
            onClick={() => onPick(r.asset_code)}
          >
            <div className="font-mono">{r.asset_code}</div>
            <div className="text-sm text-gray-700">{r.description || ""}</div>
            <div className="text-xs text-gray-600">{r.manufacturer || ""} {r.model_number || ""} • {r.category || ""}</div>
          </button>
        ))}
        {rows.length === 0 && <div className="p-3 text-sm text-gray-600">No assets found.</div>}
      </div>
    </Modal>
  );
}

/* =========================
 * QR Scan (asset_code)
 * ========================= */
function AssetQRScanModal({ open, onClose, onScanned }) {
  const [err, setErr] = useState("");
  const [manual, setManual] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    setManual("");
  }, [open]);

  if (!open) return null;

  const supportsBarcodeDetector = typeof window !== "undefined" && "BarcodeDetector" in window;

  return (
    <Modal title="Scan Asset QR" onClose={onClose}>
      {err && <div className="text-red-600 mb-2">{err}</div>}

      {supportsBarcodeDetector ? (
        <BarcodeDetectorScanner
          onResult={(txt) => txt && onScanned(txt)}
          onError={(e) => setErr(String(e))}
        />
      ) : (
        <div className="text-sm text-gray-600 mb-3">
          This browser doesn’t support camera QR scanning. Paste or type the Asset Code below.
        </div>
      )}

      <div className="border rounded p-3 mt-3">
        <div className="font-semibold mb-2">Manual Entry (fallback)</div>
        <input
          className="border rounded px-2 py-2 w-full"
          placeholder="Paste asset code…"
          value={manual}
          onChange={(e) => setManual(e.target.value)}
        />
        <div className="mt-2 flex gap-2">
          <button className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700" type="button" onClick={() => onScanned(manual)} disabled={!manual.trim()}>
            Use Value
          </button>
          <button className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300" type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}

function BarcodeDetectorScanner({ onResult, onError }) {
  const videoRef = useState(() => ({ current: null }))[0];
  const [running, setRunning] = useState(false);

  useEffect(() => {
    let stream = null;
    let raf = 0;
    let detector = null;

    const start = async () => {
      try {
        // allow QR + DataMatrix (some asset labels are DM)
        detector = new window.BarcodeDetector({ formats: ["qr_code", "data_matrix"] });
      } catch (e) {
        onError?.(e?.message || e);
        return;
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
      } catch (e) {
        onError?.("Camera permission denied or unavailable.");
        return;
      }

      if (!videoRef.current) {
        onError?.("Video element not ready.");
        return;
      }

      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setRunning(true);

      const tick = async () => {
        if (!videoRef.current || videoRef.current.readyState < 2) {
          raf = requestAnimationFrame(tick);
          return;
        }

        try {
          const codes = await detector.detect(videoRef.current);
          if (codes && codes.length) {
            const raw = codes[0]?.rawValue || "";
            if (raw) {
              onResult?.(raw);
              return;
            }
          }
        } catch {
          // ignore
        }

        raf = requestAnimationFrame(tick);
      };

      raf = requestAnimationFrame(tick);
    };

    start();

    return () => {
      cancelAnimationFrame(raf);
      setRunning(false);
      try {
        if (stream) stream.getTracks().forEach((t) => t.stop());
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className="text-sm text-gray-600 mb-2">Point your camera at the asset QR. It will auto-detect.</div>
      <div className="border rounded overflow-hidden">
        <video ref={(el) => (videoRef.current = el)} className="w-full" playsInline muted />
      </div>
      <div className="text-xs text-gray-500 mt-2">{running ? "Scanning…" : "Starting camera…"}</div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-xl w-[min(1100px,95vw)] max-h-[90vh] overflow-hidden shadow-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-semibold">{title}</div>
          <button className="px-3 py-1 rounded bg-gray-200" onClick={onClose} type="button">
            Close
          </button>
        </div>
        <div className="p-4 overflow-auto max-h-[80vh]">{children}</div>
      </div>
    </div>
  );
}
